import { inject, Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { InternalErrorHandlerService } from "../../../../../shared/_services/error-handler.service";
import { MatDialog } from "@angular/material/dialog";
import { ErrorDialogComponent } from "../../_components/error-dialog/error-dialog.component";
import { LOGIN_PROVIDERS } from "./auth.service";
import { NOT_STARTED_HERE } from "../../auth/_guards/signed-in.guard";

// The API refused to connect a sign-in to the account with its e-mail and sent the provider as the description. Anyone
// can put text in an address, so the dialog shows only our own text and names only a provider the website offers.
const EMAIL_TAKEN = "EMAIL_TAKEN";
const EMAIL_TAKEN_TEXT = "AUTH.SIGN_IN_REFUSED.EMAIL_TAKEN";
const EMAIL_TAKEN_GENERIC_TEXT = "AUTH.SIGN_IN_REFUSED.EMAIL_TAKEN_GENERIC";

// Every other refusal arrives as its own text, the API's or the website's. A link can carry any text, so a refusal is
// shown only when it is one of these word for word, with the description kept here, never the address's.
const UNKNOWN_REFUSAL_TEXT = "AUTH.SIGN_IN_REFUSED.UNKNOWN";
const KNOWN_REFUSALS: ReadonlyMap<string, string | null> = new Map([
    [NOT_STARTED_HERE, null],
    [
        "We could not create an account with these details. If you already have an account, sign in the way you created it. Otherwise, contact support.",
        null,
    ],
    ["Sign-in was stopped because the page to return to is not on our website. Please sign in again.", null],
    ["The request to link an account has expired or was already used. Please link the account again.", null],
    ["The broker connection has expired or was already used. Please connect the account again.", null],
    [
        "An account with this e-mail already exists",
        "This account's e-mail is not confirmed yet. Sign in the way you created the account, then link this sign-in in your profile. If you did not create that account, contact support.",
    ],
]);

@Injectable({
    providedIn: "root",
})
export class GeneralErrorHandlerService extends InternalErrorHandlerService {
    private readonly dialog = inject(MatDialog);
    private readonly translate = inject(TranslateService);

    public override handleForbiddenError(): boolean {
        return false;
    }

    public override handleQueryError(errorCode: string, queryParams: any): boolean {
        if (errorCode === EMAIL_TAKEN) {
            this.showEmailTaken(queryParams["errordescription"]);
            return true;
        }

        const description = KNOWN_REFUSALS.get(errorCode);
        if (description !== undefined) {
            this.dialog.open(ErrorDialogComponent, { data: { title: null, error: errorCode, description } });
            return true;
        }

        this.translate
            .get(UNKNOWN_REFUSAL_TEXT)
            .subscribe((error: string) =>
                this.dialog.open(ErrorDialogComponent, { data: { title: null, error, description: null } }),
            );
        return true;
    }

    // A refused sign-in opens the website afresh, before its texts have loaded; get() waits for them.
    private showEmailTaken(provider: unknown): void {
        const text =
            typeof provider === "string" && LOGIN_PROVIDERS.includes(provider)
                ? this.translate.get(EMAIL_TAKEN_TEXT, { provider })
                : this.translate.get(EMAIL_TAKEN_GENERIC_TEXT);

        text.subscribe((error: string) =>
            this.dialog.open(ErrorDialogComponent, { data: { title: null, error, description: null } }),
        );
    }
}
